import { useEffect, useState } from 'react';
import type { AiConfig } from '@jordan/types';
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Input,
  PageHeader,
  SelectMenu,
  SettingsPanel,
  SettingsRow,
  Spinner,
  Switch,
  Textarea,
} from '@jordan/ui';
import { AppChrome } from '../components/AppChrome';
import { apiGet, apiPut } from '../lib/api';

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  bedrock: 'Bedrock',
};

export function AiSettingsPage() {
  const [cfg, setCfg] = useState<AiConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<AiConfig>('/api/config/ai')
      .then(setCfg)
      .catch(() => setError('Failed to load AI settings.'));
  }, []);

  const toggleModel = (id: string) =>
    setCfg((p) => {
      if (!p) return p;
      const enabled = p.enabledModels.includes(id)
        ? p.enabledModels.filter((m) => m !== id)
        : [...p.enabledModels, id];
      // Keep the default valid: if it was just disabled, fall back to the first enabled model.
      const defaultModel = enabled.includes(p.defaultModel) ? p.defaultModel : (enabled[0] ?? '');
      return { ...p, enabledModels: enabled, defaultModel };
    });

  const setBedrockId = (modelId: string, value: string) =>
    setCfg((p) => (p ? { ...p, bedrockModelIds: { ...p.bedrockModelIds, [modelId]: value } } : p));

  const save = async () => {
    if (!cfg) return;
    if (cfg.enabledModels.length === 0) {
      setError('Enable at least one model.');
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await apiPut<AiConfig>('/api/config/ai', {
        defaultModel: cfg.defaultModel,
        enabledModels: cfg.enabledModels,
        systemPrompt: cfg.systemPrompt,
        maxTokens: cfg.maxTokens,
        bedrockModelIds: cfg.bedrockModelIds,
      });
      setCfg(saved);
      setMessage('AI settings saved.');
    } catch {
      setError('Could not save — check the values.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppChrome>
      <PageHeader
        title={
          <>
            Configuration - <span className="fw-normal">AI</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Configuration' }, { label: 'AI' }]} />}
      />

      <div className="content">
        {message && (
          <Alert variant="success" icon="ph-check-circle" dismissible>
            {message}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {error}
          </Alert>
        )}

        {!cfg ? (
          <Spinner />
        ) : (
          <SettingsPanel
            title="AI chat"
            description="Tune the models and behaviour of the AI section. Provider API keys are set in the environment (Secret Manager), not here."
          >
            <SettingsRow title="Providers" description="A provider is usable once its API key is set in the environment.">
              <div className="d-flex flex-wrap gap-2">
                {Object.entries(cfg.providers).map(([id, p]) => (
                  <Badge key={id} variant={p.configured ? 'success' : 'secondary'}>
                    {PROVIDER_LABELS[id] ?? id}: {p.configured ? 'configured' : 'not configured'}
                  </Badge>
                ))}
              </div>
            </SettingsRow>

            <SettingsRow title="Enabled models" description="Models offered in the chat composer.">
              <div className="d-flex flex-column gap-2">
                {cfg.models.map((m) => (
                  <Switch
                    key={m.id}
                    label={`${m.label}  ·  ${PROVIDER_LABELS[m.provider] ?? m.provider}`}
                    checked={cfg.enabledModels.includes(m.id)}
                    onChange={() => toggleModel(m.id)}
                  />
                ))}
              </div>
            </SettingsRow>

            <SettingsRow title="Default model" description="Pre-selected in the composer for new chats.">
              <div style={{ maxWidth: 280 }}>
                <SelectMenu
                  icon="ph-cpu"
                  value={cfg.defaultModel}
                  onChange={(v) => setCfg((p) => (p ? { ...p, defaultModel: v } : p))}
                  options={cfg.models
                    .filter((m) => cfg.enabledModels.includes(m.id))
                    .map((m) => ({ value: m.id, label: m.label }))}
                />
              </div>
            </SettingsRow>

            <SettingsRow title="System prompt" description="Prepended to every conversation (optional).">
              <Textarea
                rows={4}
                value={cfg.systemPrompt}
                onChange={(e) => setCfg((p) => (p ? { ...p, systemPrompt: e.target.value } : p))}
              />
            </SettingsRow>

            <SettingsRow title="Max tokens" description="Maximum length of each reply.">
              <div style={{ maxWidth: 200 }}>
                <Input
                  type="number"
                  value={cfg.maxTokens}
                  onChange={(e) => setCfg((p) => (p ? { ...p, maxTokens: Number(e.target.value) } : p))}
                />
              </div>
            </SettingsRow>

            {cfg.models.some((m) => m.provider === 'bedrock') && (
              <SettingsRow
                title="Bedrock model IDs"
                description="The Amazon Bedrock invocation id (or inference-profile id) for each Claude-on-Bedrock model. Find these in AWS → Bedrock → Model access and verify they match what you enabled."
              >
                <div className="d-flex flex-column gap-2" style={{ maxWidth: 480 }}>
                  {cfg.models
                    .filter((m) => m.provider === 'bedrock')
                    .map((m) => (
                      <Input
                        key={m.id}
                        label={m.label}
                        className="font-monospace"
                        value={cfg.bedrockModelIds[m.id] ?? ''}
                        onChange={(e) => setBedrockId(m.id, e.target.value)}
                        placeholder="us.anthropic.claude-...-v1:0"
                      />
                    ))}
                </div>
              </SettingsRow>
            )}

            <SettingsRow title="" description="">
              <Button variant="primary" disabled={busy} onClick={save}>
                Save changes
              </Button>
            </SettingsRow>
          </SettingsPanel>
        )}
      </div>
    </AppChrome>
  );
}
